import React from "react";
import { Card, Icon, Popover } from "../../components/ui";
import { KPI } from "../kpiInfo";

// Small building blocks shared by every Dashboard v2 tab.

/** The "i" next to a KPI: tap for its formula (kpiInfo.js). */
export function InfoTip({ k }) {
  const def = KPI[k];
  if (!def) return null;
  return (
    <Popover
      label={def[2]}
      side="bottom"
      align="start"
      trigger={
        <button type="button" className="dash-info" aria-label={`How ${def[2]} is calculated`}>
          <Icon name="Info" size="xs" />
        </button>
      }
    >
      <div className="dash-info__pop">
        <b>{def[2]}</b>
        <p>{def[3]}</p>
      </div>
    </Popover>
  );
}

export function CardHead({ title, info, meta, children }) {
  return (
    <div className="dash-card__head">
      <h3 className="dash-card__title">
        {title}
        {info ? <InfoTip k={info} /> : null}
      </h3>
      {meta ? <span className="dash-card__meta">{meta}</span> : null}
      {children ? <div className="dash-card__tools">{children}</div> : null}
    </div>
  );
}

/** A dashboard card: the ui Card with the page's padding + head. */
export function DashCard({ title, info, meta, tools, className = "", children, ...rest }) {
  return (
    <Card padding="none" className={`dash-card ${className}`} {...rest}>
      {title ? (
        <CardHead title={title} info={info} meta={meta}>
          {tools}
        </CardHead>
      ) : null}
      {children}
    </Card>
  );
}

/** ▲ 12.4% / ▼ 3 pts — `good` decides the colour (lower can be better). */
export function Delta({ value, suffix = "%", digits = 1, invert = false, label }) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
  const n = Number(value);
  const up = n > 0;
  const flat = n === 0;
  const good = flat ? null : invert ? !up : up;
  return (
    <span className={`dash-delta ${flat ? "is-flat" : good ? "is-up" : "is-down"}`}>
      <span aria-hidden="true">{flat ? "■" : up ? "▲" : "▼"}</span> {Math.abs(n).toFixed(digits)}
      {suffix}
      {label ? <span className="dash-delta__label"> {label}</span> : null}
    </span>
  );
}

/**
 * One cell of a stat grid. `onClick` makes the whole cell clickable (jump to the
 * queue filter) through a stretched button UNDER the content — never a <button>
 * wrapping it: the legacy theme gives every bare button `height: 49px`, which
 * collapsed the value row to 0px (Open actions / Zombie deals showed no number
 * on prod, 21 Oct), and the "i" inside would be a button in a button.
 */
export function Stat({ label, value, foot, info, tone, onClick }) {
  return (
    <div className={`dash-stat${tone ? ` dash-stat--${tone}` : ""}${onClick ? " dash-stat--link" : ""}`}>
      {onClick ? <button type="button" className="dash-stat__hit" aria-label={`${label}: ${value}`} onClick={onClick} /> : null}
      <span className="dash-stat__l">
        {label}
        {info ? <InfoTip k={info} /> : null}
      </span>
      <span className="dash-stat__v dash-num">{value}</span>
      {foot ? <span className="dash-stat__f">{foot}</span> : null}
    </div>
  );
}

/**
 * A ruled grid of Stat cells with as many columns as cells (up to 6; 8 → 4×2),
 * so no row is left with an empty grey filler cell.
 */
export function StatGrid({ children, className = "" }) {
  const n = React.Children.toArray(children).filter(Boolean).length;
  const cols = n === 8 ? 4 : n === 7 ? 4 : Math.min(6, Math.max(1, n));
  return <div className={`dash-stats dash-stats--n${cols} ${className}`.trim()}>{children}</div>;
}

export const SectionLabel = ({ children }) => <div className="dash-section-label">{children}</div>;

/** A ranked list row: rank/avatar · title + subtitle · right value. */
export function ListRow({ lead, title, sub, right, rightSub, action }) {
  return (
    <div className="dash-li">
      {lead}
      <div className="dash-li__body">
        <div className="dash-li__t">{title}</div>
        {sub ? <div className="dash-li__s">{sub}</div> : null}
      </div>
      {right != null ? (
        <div className="dash-li__r dash-num">
          {right}
          {rightSub ? <small>{rightSub}</small> : null}
        </div>
      ) : null}
      {action}
    </div>
  );
}

export const Rank = ({ n }) => <span className={`dash-rank${n === 1 ? " is-first" : ""}`}>{n}</span>;

export const Avatar = ({ name, initials, i = 0 }) => (
  <span className={`dash-avatar dash-bg--c${(i % 6) + 1}`} aria-hidden="true">{initials || (name || "?").slice(0, 2).toUpperCase()}</span>
);
